import React, { useEffect, useState } from "react";
import api, { INR } from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search as SearchIcon, MapPin, Calendar, Plus, X } from "lucide-react";

const LOCALITIES = ["Vijay Nagar", "Palasia", "Bhawarkuan", "AB Road", "Rau", "Sudama Nagar",
  "Old Palasia", "Nipania", "MR-10", "Bengali Square", "Scheme 78", "Geeta Bhawan"];
const CATS = ["Entire Property", "Private Room", "Shared Room", "PG", "Hostel", "Flatmate Needed"];

export default function Searches() {
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", city: "", budget_min: 5000, budget_max: 20000,
    move_in_date: new Date().toISOString().slice(0, 10),
    property_types: ["Private Room"], preferred_localities: [],
    preferred_gender: "any", food_pref: "any", sharing_preference: "private",
    furnished: "any", occupation: "", notes: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    try {
      const r = await api.get("/my/searches");
      setMine(r.data.searches);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (key, val) => {
    const arr = form[key];
    upd(key, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const submit = async () => {
    if (!form.title || !form.budget_max) { toast.error("Title and budget required"); return; }
    try {
      await api.post("/searches", form);
      toast.success("Search posted — we'll notify you of matches");
      setOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const close = async (id) => {
    await api.delete(`/searches/${id}`);
    await load();
    toast.success("Search closed");
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Housing searches</div>
            <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900">Tell owners what you want</h1>
            <p className="text-stone-600 mt-1">Post a search and let owners come to you.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-search" className="brand-accent rounded-full">
                <Plus className="w-4 h-4 mr-1" /> New search
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New housing search</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">What are you looking for?</Label>
                  <Input data-testid="search-title" value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="e.g. Private room near tech park" />
                </div>
                <div>
                  <Label className="text-xs">Budget min (₹)</Label>
                  <Input data-testid="search-budget-min" type="number" value={form.budget_min} onChange={(e) => upd("budget_min", Number(e.target.value))} />
                </div>
                <div>
                  <Label className="text-xs">Budget max (₹)</Label>
                  <Input data-testid="search-budget-max" type="number" value={form.budget_max} onChange={(e) => upd("budget_max", Number(e.target.value))} />
                </div>
                <div>
                  <Label className="text-xs">Move-in date</Label>
                  <Input type="date" value={form.move_in_date} onChange={(e) => upd("move_in_date", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Sharing</Label>
                  <Select value={form.sharing_preference} onValueChange={(v) => upd("sharing_preference", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["any","private","shared","entire"].map(v => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Preferred gender</Label>
                  <Select value={form.preferred_gender} onValueChange={(v) => upd("preferred_gender", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["any","male","female"].map(v => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Food</Label>
                  <Select value={form.food_pref} onValueChange={(v) => upd("food_pref", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["any","veg","eggetarian"].map(v => <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Preferred categories</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {CATS.map((c) => (
                      <button type="button" key={c} onClick={() => toggle("property_types", c)}
                        className={`chip cursor-pointer ${form.property_types.includes(c) ? "bg-[#2A4B3C]/10 text-[#2A4B3C]" : ""}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Preferred localities</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {LOCALITIES.map((l) => (
                      <button type="button" key={l} onClick={() => toggle("preferred_localities", l)}
                        className={`chip cursor-pointer ${form.preferred_localities.includes(l) ? "bg-[#C84B31]/10 text-[#C84B31]" : ""}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Anything owners should know" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="search-submit" className="brand-accent rounded-full" onClick={submit}>Post search</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-3">
          {mine.length === 0 && <div className="text-center py-16 text-stone-500 border border-dashed border-stone-300 rounded-2xl">No searches yet. Create one so owners can reach out.</div>}
          {mine.map((s) => (
            <div key={s.id} data-testid={`search-${s.id}`} className="p-5 bg-white border border-stone-200 rounded-2xl">
              <div className="flex items-start gap-3">
                <SearchIcon className="w-4 h-4 text-brand mt-1" />
                <div className="flex-1">
                  <div className="font-medium text-stone-900">{s.title}</div>
                  <div className="text-sm text-stone-600 mt-1 flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {(s.preferred_localities || []).join(", ") || s.city}</span>
                    <span>{INR(s.budget_min)} – {INR(s.budget_max)}</span>
                    {s.move_in_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(s.move_in_date).toLocaleDateString()}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(s.property_types || []).map((t) => <span key={t} className="chip">{t}</span>)}
                    {s.preferred_gender !== "any" && <span className="chip capitalize">{s.preferred_gender} pref</span>}
                    {s.food_pref !== "any" && <span className="chip capitalize">{s.food_pref}</span>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => close(s.id)} data-testid={`close-${s.id}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
